import type { SpriteId } from "./contracts.js";
import type { RuntimeAtlas as Atlas } from "./atlasContract.js";
import {
  type CameraState,
  geoToTile,
  type Rotation,
  screenToTile,
  tileToScreen,
  type Zoom,
} from "./projection.js";
import type { RenderPose, RenderSprite, RenderTerrainChunk } from './rendererAdapter.js';
export const TERRAIN_CHUNK_TILES = 16;
export const terrainChunkTilesForLevel=(level:1|2|3):number=>level===3?8:TERRAIN_CHUNK_TILES;
export const terrainChunkMetrics=(level:1|2|3)=>{const tiles=terrainChunkTilesForLevel(level),sizePx:[number,number]=[64*level*tiles,32*level*tiles],pixels=sizePx[0]*sizePx[1];return{level,tiles,sizePx,pixels,bytes:pixels*4};};
export const TERRAIN_WIDTH = 2700;
export const TERRAIN_HEIGHT = 2800;
export type TerrainKind = "water" | "park" | "urban" | "beach" | "road";
export type TerrainTile = { kind: Exclude<TerrainKind, 'building'> } | { kind: 'building'; height: number };
const terrainKinds: TerrainKind[] = ["water", "park", "urban", "beach", "road"];
export const terrainUnderlayColor = (): string => "#303638";
export interface TerrainRaster {
  width: number;
  height: number;
  indices: Uint8Array;
}
export function terrainTileAt(
  raster: TerrainRaster,
  tile: readonly [number, number],
): TerrainTile | undefined {
  const column = Math.floor(tile[0]), row = Math.floor(tile[1]);
  if (column < 0 || row < 0 || column >= raster.width || row >= raster.height) return undefined;
  const index = raster.indices[row * raster.width + column]!;
  if (index <= 4) return { kind: terrainKinds[index]! };
  if (index >= 9 && index <= 24) return { kind: 'building', height: index - 8 };
  throw new Error(`terrain index ${index} is reserved or invalid`);
}
export function terrainKindAtTile(
  raster: TerrainRaster,
  tile: readonly [number, number],
): TerrainKind | undefined {
  const decoded = terrainTileAt(raster, tile);
  return decoded?.kind === 'building' ? 'urban' : decoded?.kind;
}
export function terrainKindAtGeo(
  raster: TerrainRaster,
  geo: [longitude: number, latitude: number],
): TerrainKind | undefined {
  return terrainKindAtTile(raster, geoToTile(geo));
}
export function roadMaskAtTile(raster: TerrainRaster, tile: readonly [number, number]): number {
  const [x, y] = tile.map(Math.floor) as [number, number];
  return ([[1, 0], [0, 1], [-1, 0], [0, -1]] as const).reduce(
    (mask, [dx, dy], bit) => terrainTileAt(raster, [x + dx, y + dy])?.kind === 'road'
      ? mask | (1 << bit) : mask,
    0,
  );
}
export const rotatedRoadMask = (mask: number, rotation: Rotation): number =>
  ((mask << rotation) | (mask >> (4 - rotation))) & 15;
export const fnv1a = (value: string): number => {
  let hash = 2166136261;
  for (const byte of new TextEncoder().encode(value)) hash = Math.imul(hash ^ byte, 16777619) >>> 0;
  return hash >>> 0;
};
export const treeAtTile = (raster: TerrainRaster, tile: readonly [number, number]): { stableId: string; tile: [number, number] } | undefined => {
  const [tx, ty] = tile.map(Math.floor) as [number, number];
  return terrainTileAt(raster, [tx, ty])?.kind === 'park' && fnv1a(`${tx},${ty}`) < 1073741824
    ? { stableId: `tree:${tx},${ty}`, tile: [tx + .5, ty + .5] } : undefined;
};
export type ChunkBuilder = (
  chunkX: number,
  chunkY: number,
  rotation: Rotation,
  zoom: Zoom,
  pixelRatio: number,
) => HTMLCanvasElement;
export class TerrainChunks {
  private cache = new Map<string, HTMLCanvasElement>();
  private bytes = 0;
  constructor(
    private build: ChunkBuilder,
    private maxBytes = 64 * 1024 * 1024,
  ) {
    if (maxBytes < 1) {
      throw new Error("terrain cache byte limit must be positive");
    }
  }
  get(
    chunkX: number,
    chunkY: number,
    rotation: Rotation,
    zoom: Zoom,
    pixelRatio = 1,
  ): HTMLCanvasElement {
    const key = `${rotation}:${zoom}:${pixelRatio}:${chunkX}:${chunkY}`,
      cached = this.cache.get(key);
    if (cached) {
      this.cache.delete(key);
      this.cache.set(key, cached);
      return cached;
    }
    const value = this.build(chunkX, chunkY, rotation, zoom, pixelRatio);
    this.cache.set(key, value);
    this.bytes += value.width * value.height * 4;
    while (this.bytes > this.maxBytes && this.cache.size > 1) {
      const oldest = this.cache.keys().next().value!;
      const evicted = this.cache.get(oldest)!;
      this.bytes -= evicted.width * evicted.height * 4;
      this.cache.delete(oldest);
    }
    return value;
  }
  clear(): void {
    this.cache.clear();
    this.bytes = 0;
  }
  get size(): number {
    return this.cache.size;
  }
}
export function terrainOpacity(surface: boolean, underground: boolean): number {
  return !surface && underground ? 0.28 : 1;
}
export const prepareTerrainContext = (
  context: CanvasRenderingContext2D,
): void => {
  context.imageSmoothingEnabled = false;
};
export function decodeTerrainPixels(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  expected = { width: TERRAIN_WIDTH, height: TERRAIN_HEIGHT },
): Uint8Array {
  if (
    width !== expected.width || height !== expected.height ||
    pixels.length !== width * height * 4
  ) throw new Error("terrain dimensions");
  const indices = new Uint8Array(width * height);
  for (let i = 0; i < indices.length; i += 1) {
    const offset = i * 4, index = pixels[offset]!;
    if (
      (index >= 5 && index <= 8) || index >= 25 || pixels[offset + 1] !== index ||
      pixels[offset + 2] !== index || pixels[offset + 3] !== 255
    ) throw new Error("terrain palette");
    indices[i] = index;
  }
  return indices;
}
export function decodeTerrainImage(image: HTMLImageElement): TerrainRaster {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("terrain canvas unavailable");
  context.drawImage(image, 0, 0);
  return {
    width: canvas.width,
    height: canvas.height,
    indices: decodeTerrainPixels(
      context.getImageData(0, 0, canvas.width, canvas.height).data,
      canvas.width,
      canvas.height,
    ),
  };
}

export interface TerrainChunkLayout {
  offset: [number, number];
  size: [number, number];
}
type GroundFrame = Pick<
  Atlas["sprites"][SpriteId]["frames"][number],
  "rect" | "anchor"
>;
/** Exact local isometric bounds: every tile frame, including its anchor overhang, fits. */
export function terrainChunkLayout(
  rotation: Rotation,
  scale: number,
  frames: readonly GroundFrame[],
  tiles=TERRAIN_CHUNK_TILES,
): TerrainChunkLayout {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let y = 0; y < tiles; y += 1) {
    for (let x = 0; x < tiles; x += 1) {
      const [sx, sy] = tileToScreen([x, y], {
        center: [0, 0],
        zoom: scale as Zoom,
        rotation,
      });
      for (const frame of frames) {
        const [, , w, h] = frame.rect,
          left = sx - frame.anchor[0],
          top = sy - frame.anchor[1];
        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, left + w);
        maxY = Math.max(maxY, top + h);
      }
    }
  }
  return {
    offset: [-Math.floor(minX), -Math.floor(minY)],
    size: [Math.ceil(maxX - minX), Math.ceil(maxY - minY)],
  };
}
export class TerrainPainter {
  readonly chunks: TerrainChunks;
  // At 3×/DPR2 an exact isometric chunk is roughly 0.8M pixels; retain <=16 (~50 MiB RGBA).
  constructor(
    private raster: TerrainRaster,
    private atlas: Atlas,
    atlasImages: CanvasImageSource | readonly CanvasImageSource[],
  ) {
    this.atlasImages = Array.isArray(atlasImages) ? atlasImages : [atlasImages];
    this.chunks = new TerrainChunks((x, y, r, z, p) =>
      this.buildChunk(x, y, r, z, p)
    );
  }
  private readonly atlasImages: readonly CanvasImageSource[];
  submissions(camera: CameraState, viewport: [number, number], pixelRatio = 1, alpha = 1): RenderTerrainChunk[] {
    const activeLevel=(this.atlas.level??1) as 1|2|3, screenScale=camera.zoom*pixelRatio,
      renderCamera={...camera,zoom:screenScale as Zoom}, frames=terrainKinds.flatMap(kind=>this.atlas.sprites[`ground/${kind}` as SpriteId].frames),
      layout=terrainChunkLayout(camera.rotation,activeLevel,frames), corners:[[number,number],[number,number],[number,number],[number,number]]=[[0,0],[viewport[0],0],[0,viewport[1]],[viewport[0],viewport[1]]],
      tiles=corners.map(([x,y])=>screenToTile([x-viewport[0]/2,y-viewport[1]/2],renderCamera)),
      minX=Math.max(0,Math.floor(Math.min(...tiles.map(tile=>tile[0]))/TERRAIN_CHUNK_TILES)-1), maxX=Math.min(Math.ceil(this.raster.width/TERRAIN_CHUNK_TILES)-1,Math.floor(Math.max(...tiles.map(tile=>tile[0]))/TERRAIN_CHUNK_TILES)+1),
      minY=Math.max(0,Math.floor(Math.min(...tiles.map(tile=>tile[1]))/TERRAIN_CHUNK_TILES)-1), maxY=Math.min(Math.ceil(this.raster.height/TERRAIN_CHUNK_TILES)-1,Math.floor(Math.max(...tiles.map(tile=>tile[1]))/TERRAIN_CHUNK_TILES)+1), out:RenderTerrainChunk[]=[];
    for(let chunkY=minY;chunkY<=maxY;chunkY+=1)for(let chunkX=minX;chunkX<=maxX;chunkX+=1){
      const origin:[number,number]=[chunkX*TERRAIN_CHUNK_TILES,chunkY*TERRAIN_CHUNK_TILES], originTile:[number,number]=[origin[0]+.5,origin[1]+.5], chunkTiles:RenderSprite[]=[];
      for(let y=origin[1];y<Math.min(origin[1]+TERRAIN_CHUNK_TILES,this.raster.height);y+=1)for(let x=origin[0];x<Math.min(origin[0]+TERRAIN_CHUNK_TILES,this.raster.width);x+=1){
        const decoded=terrainTileAt(this.raster,[x,y])!, spriteId=(decoded.kind==='building'?'ground/urban':`ground/${decoded.kind}`) as SpriteId,
          frameIndex=decoded.kind==='road'?rotatedRoadMask(roadMaskAtTile(this.raster,[x,y]),camera.rotation):camera.rotation,
          frame=this.atlas.sprites[spriteId].frames[frameIndex]!, tile:[number,number]=[x+.5,y+.5], unitAnchor=tileToScreen(tile,{center:[0,0],zoom:1,rotation:camera.rotation}), pose:RenderPose={tile,worldMeters:[tile[0]*20-26000,28000-tile[1]*20],alignment:null,elevation:0,layerVisible:true};
        chunkTiles.push({kind:'sprite',instanceId:`terrain:${x}:${y}`,spriteId,level:activeLevel,page:frame.page??0,frameIndex,frameRect:[...frame.rect],frameAnchor:[...frame.anchor],unitAnchor,clip:null,alpha:1,visible:true,pose,sort:{rotatedX:x,rotatedY:y,depth:x+y,height:0,phase:0,occlusionGroup:null}});
      }
      out.push({kind:'terrain-chunk',instanceId:`terrain:${chunkX}:${chunkY}`,cacheKey:`${this.atlas.sourceRevision}:${chunkX}:${chunkY}:${camera.rotation}:${activeLevel}`,level:activeLevel,rotation:camera.rotation,unitAnchor:tileToScreen(originTile,{center:[0,0],zoom:1,rotation:camera.rotation}),nativeOffset:layout.offset,nativeSize:layout.size,tiles:chunkTiles,alpha,visible:true});
    }
    return out;
  }
  private buildChunk(
    chunkX: number,
    chunkY: number,
    rotation: Rotation,
    zoom: Zoom,
    pixelRatio: number,
  ): HTMLCanvasElement {
    const scale = this.atlas.level ?? 1,
      camera = {
        center: [0, 0] as [number, number],
        zoom: scale as Zoom,
        rotation,
      },
      frames = terrainKinds.flatMap((kind) =>
        this.atlas.sprites[`ground/${kind}` as SpriteId].frames
      ),
      layout = terrainChunkLayout(rotation, scale, frames),
      canvas = document.createElement("canvas");
    canvas.width = layout.size[0];
    canvas.height = layout.size[1];
    const context = canvas.getContext("2d");
    if (!context) throw new Error("terrain canvas unavailable");
    prepareTerrainContext(context);
    const origin: [number, number] = [
        chunkX * TERRAIN_CHUNK_TILES,
        chunkY * TERRAIN_CHUNK_TILES,
      ],
      originScreen = tileToScreen([origin[0] + .5, origin[1] + .5], camera);
    for (
      let y = origin[1];
      y < Math.min(origin[1] + TERRAIN_CHUNK_TILES, this.raster.height);
      y += 1
    ) {
      for (
        let x = origin[0];
        x < Math.min(origin[0] + TERRAIN_CHUNK_TILES, this.raster.width);
        x += 1
      ) {
        if (x < 0 || y < 0) continue;
        const tile = terrainTileAt(this.raster, [x, y])!,
          // Chunk bounds above guarantee a valid raster tile.
          spriteId = tile.kind === 'building' ? 'ground/urban' : `ground/${tile.kind}`,
          frame = tile.kind === 'road'
            ? this.atlas.sprites['ground/road'].frames[rotatedRoadMask(roadMaskAtTile(this.raster, [x, y]), rotation)]!
            : this.atlas.sprites[spriteId as SpriteId].frames[rotation]!,
          screen = tileToScreen([x + .5, y + .5], camera),
          [sx, sy, sw, sh] = frame.rect;
        context.drawImage(
          this.atlasImages[frame.page ?? 0]!,
          sx,
          sy,
          sw,
          sh,
          Math.round(
            screen[0] - originScreen[0] + layout.offset[0] -
              frame.anchor[0],
          ),
          Math.round(
            screen[1] - originScreen[1] + layout.offset[1] -
              frame.anchor[1],
          ),
          sw,
          sh,
        );
      }
    }
    return canvas;
  }
  paint(
    context: CanvasRenderingContext2D,
    camera: CameraState,
    viewport: [number, number],
    pixelRatio = 1,
  ): void {
    prepareTerrainContext(context);
    const screenScale = camera.zoom * pixelRatio,
      activeLevel = this.atlas.level ?? 1,
      scale = screenScale / activeLevel,
      renderCamera = { ...camera, zoom: screenScale as Zoom },
      frames = terrainKinds.flatMap((kind) =>
        this.atlas.sprites[`ground/${kind}` as SpriteId].frames
      ),
      layout = terrainChunkLayout(camera.rotation, activeLevel, frames);
    const corners: [number, number][] = [[0, 0], [viewport[0], 0], [
      0,
      viewport[1],
    ], [viewport[0], viewport[1]]];
    const tiles = corners.map(([x, y]) =>
      screenToTile([x - viewport[0] / 2, y - viewport[1] / 2], renderCamera)
    );
    const minX = Math.max(
        0,
        Math.floor(
          Math.min(...tiles.map((tile) => tile[0])) / TERRAIN_CHUNK_TILES,
        ) - 1,
      ),
      maxX = Math.min(
        Math.ceil(this.raster.width / TERRAIN_CHUNK_TILES) - 1,
        Math.floor(
          Math.max(...tiles.map((tile) => tile[0])) / TERRAIN_CHUNK_TILES,
        ) + 1,
      ),
      minY = Math.max(
        0,
        Math.floor(
          Math.min(...tiles.map((tile) => tile[1])) / TERRAIN_CHUNK_TILES,
        ) - 1,
      ),
      maxY = Math.min(
        Math.ceil(this.raster.height / TERRAIN_CHUNK_TILES) - 1,
        Math.floor(
          Math.max(...tiles.map((tile) => tile[1])) / TERRAIN_CHUNK_TILES,
        ) + 1,
      );
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const screen = tileToScreen([
          x * TERRAIN_CHUNK_TILES + .5,
          y * TERRAIN_CHUNK_TILES + .5,
        ], renderCamera);
        const chunk = this.chunks.get(x, y, camera.rotation, activeLevel as Zoom, 1);
        context.drawImage(
          chunk,
          Math.round(viewport[0] / 2 + screen[0] - layout.offset[0] * scale),
          Math.round(viewport[1] / 2 + screen[1] - layout.offset[1] * scale),
          chunk.width * scale,
          chunk.height * scale,
        );
      }
    }
  }
}
